import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { api, ErroApi } from '../api';
import { Pendencias } from '../Pendencias';
import {
  descartarCompactacaoPonto, descartarCompactacaoUmidade,
  gravarCompactacaoCabecalho, gravarCompactacaoPontos,
  gravarCompactacaoUmidades, verCompactacao,
} from '../api/compactacao';
import type { EnsaioCompactacao } from '../api/compactacao';
import {
  AncoraDePendencias, BlocoDaFicha, CabecalhoDaFicha, EscolhaDeBalanca,
  EtapasDaFicha, FaixaDaFicha, JanelaDeDescarte, LaudoDaFicha, RodapeDeEtapas,
  fichaCongelada, useEquipamentos, useEtapa, useFicha,
} from '../Ficha';
import type { Etapa, Indicador } from '../Ficha';
import { numero, paraNumero, paraCampo, plural, rotulo } from '../formato';
import { FotosDoRegistro } from '../FotosDoRegistro';
import { Achado, Plano, Serie } from '../Grafico';
import {
  Aviso, Campo, CampoNumerico, Carregando, ErroDaApi, Escolha,
  EscolhaPorCodigo, RespostaDaAcao, Selo, TabelaDeLancamento, comoCodigo,
} from '../ui';
import type { ColunaDeLancamento, LinhaDeLancamento, Opcao } from '../ui';
import {
  LembreteDaEstufa, SecaDaLinha, WDaLinha, naEstufaDaFicha, useEstufaDoEnsaio,
} from './NaEstufa';
import type { Estufa } from './NaEstufa';
import { useSessao } from '../sessao';
import { AvisoDaRepeticaoAberta } from './Triviais';
import { useLinhasDaRepeticao } from './repeticao/Repeticao';
import { PontosDaCompactacaoEmLeitura, RepeticaoCompactacao } from './repeticao/RepeticaoCompactacao';
import { TentativasAnteriores } from './repeticao/TentativasAnteriores';
import { TrilhaDoEnsaio } from './repeticao/TrilhaDoEnsaio';

/**
 * Lançar uma compactação — do cilindro ao PDF (ABNT NBR 7182:2025).
 *
 * ─── No Carbon (onda 237) ──────────────────────────────────────────────
 *
 * A espinha de `Ficha.tsx`: cabeçalho, faixa (w ótima e γd máx sempre à
 * vista), três etapas — condições, pontos, fotos e laudo. Na etapa dos
 * pontos, a CURVA DE COMPACTAÇÃO aparece na tela com a de saturação
 * (decisão 3 do Humberto, 02/10/2026) — a mesma curva do laudo,
 * reconstruída com os coeficientes que o banco resolveu (0022).
 *
 * As duas baterias de pesagem têm a sua balança (RN-215, 0217): o
 * cilindro com solo, numa balança de capacidade, e as cápsulas, numa de
 * resolução. Uma escolha por bateria.
 *
 * As casas são as da norma (7.2 e 7.3): γd máx com 0,001 g/cm³ e w ótima
 * com 0,1 %. O w de cada cápsula fica com duas casas — é intermediário.
 *
 * ─── O que a tela NÃO pede ─────────────────────────────────────────────
 *
 * A massa do cilindro, o VOLUME do cilindro e a tara da cápsula. Os três
 * são propriedade do equipamento e vêm da aferição vigente na data do
 * ensaio. O técnico escolhe QUAL cilindro usou, e o resto o banco sabe.
 *
 * Isso não é economia de digitação: é o que faz o laudo de 2026 continuar
 * dando o mesmo número depois de o cilindro ser reaferido em 2028.
 */

/*
 * Os códigos são os do catálogo da 0039, não invenções desta tela.
 * `cilindro` e `molde` estão na lista porque a 0039 os aposentou sem
 * apagar: o parque tem equipamento antigo cadastrado neles, e esconder o
 * cilindro de quem já o tem seria consertar o catálogo criando um
 * impedimento na bancada.
 */
const CAT_CILINDRO = [
  'cilindro_proctor', 'cilindro_cbr', 'cilindro_hilf', 'cilindro', 'molde',
];
/* Onda 210: só as cápsulas de compactação — «Só de compactação»,
   Humberto, 24/09/2026. O ponto já lançado continua mostrando a cápsula
   que usou, seja qual for a categoria dela. */
const CAT_CAPSULA = ['capsula_compactacao'];
const CAT_SOQUETE = ['soquete'];

/** As casas da NBR 7182:2025 (7.2 e 7.3) e as dos intermediários. */
export const CASAS = { gamaMax: 3, wOtima: 1, gama: 3, wPonto: 1, wCapsula: 2 } as const;

const num = (v: unknown, casas: number) => numero(v, casas);

export function Compactacao() {
  const { id = '' } = useParams();
  const [d, setD] = useState<EnsaioCompactacao | null>(null);
  const equip = useEquipamentos();
  const [gente, setGente] = useState<{ id: string; nome: string }[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  const ficha = useFicha(id, 'NBR 7182', versao);
  /* As cápsulas na estufa e o carimbo das pesagens (onda 294, RN-241). */
  const estufa = useEstufaDoEnsaio(id, versao);
  /* R2 (onda 323): com a repetição aberta, a etapa Pontos lista e lança os
     pontos DELA. */
  const rep = useLinhasDaRepeticao<EnsaioCompactacao>(
    id, 'compactacao', d?.repeticaoAberta, versao);
  const { pode } = useSessao();
  /* A Interpretação (onda 323) existe só para quem interpreta. */
  const interpreta = pode('ensaio.interpretar');
  const total = interpreta ? 4 : 3;

  const carregar = useCallback(async () => {
    try { setD(await verCompactacao(id)); } catch (e) { setErro(e); }
  }, [id]);
  useEffect(() => { void carregar(); }, [carregar]);

  useEffect(() => {
    void (async () => {
      try {
        const u = await api<{ id: string; nome: string }[]>('/usuarios');
        setGente(Array.isArray(u) ? u : []);
      } catch { /* o seletor cai vazio, e a tela diz isso */ }
    })();
  }, []);

  const gravar = useCallback(async (
    marca: string, chamada: () => Promise<EnsaioCompactacao>,
  ) => {
    setSalvando(marca); setErro(null); setOk(null);
    try {
      setD(await chamada());
      setOk('Gravado.');
      setVersao((v) => v + 1);
      return true;
    } catch (e) { setErro(e); return false; }
    finally { setSalvando(null); }
  }, []);

  const opcoes = (cats: string[]): Opcao[] => equip
    .filter((e) => cats.includes(e.categoria))
    /* `detalhe` alimenta o filtro do `EscolhaPorCodigo`: quem digita
       «118» acha CAP-0118, e quem digita «cbr» acha os cilindros. */
    .map((e) => ({ valor: e.id, texto: e.codigo_patrimonial,
      detalhe: e.nome }));

  const campoErro = (c: string) =>
    erro instanceof ErroApi ? erro.campo(c) : undefined;

  const c = d?.cabecalho;
  /* O que a etapa Pontos mostra: a repetição aberta, se houver (R2). A faixa,
     as pendências e o laudo continuam com a vigente (D5). */
  const repAberta = d?.repeticaoAberta != null;
  const daBancada: EnsaioCompactacao | null = d && repAberta && rep.atual
    ? { ...d, cabecalho: rep.atual.cabecalho ?? d.cabecalho, pontos: rep.atual.pontos ?? [],
        umidades: rep.atual.umidades ?? [], curva: rep.atual.curva ?? null,
        balanca: rep.atual.balanca ?? d.balanca,
        balancaUmidade: rep.atual.balancaUmidade ?? d.balancaUmidade }
    : d;
  const cb = daBancada?.cabecalho;
  const temCondicoes = c != null;
  const validos: number = cb?.pontos ?? 0;
  const temPontos = c?.massa_especifica_seca_maxima_norma != null
    && !!d?.balanca && !!d?.balancaUmidade;
  const [etapa, irPara] = useEtapa(total, !temCondicoes ? 1 : !temPontos ? 2 : total);

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d || !daBancada) return <Carregando texto="Abrindo o ensaio" />;
  const congelado = fichaCongelada(ficha?.status);
  const naTentativa = repAberta ? ` · tentativa ${d.repeticaoAberta}` : '';
  const lendoRep = repAberta && rep.carregando;

  const etapas: Etapa[] = [
    { nome: 'Condições', feita: temCondicoes,
      resumo: temCondicoes
        ? [c?.cilindro ? `cilindro ${c.cilindro}` : null,
          c?.n_camadas && c?.n_golpes ? `${c.n_camadas} camadas × ${c.n_golpes} golpes` : null]
          .filter(Boolean).join(' · ')
        : 'a gravar' },
    { nome: 'Pontos', feita: repAberta ? cb?.massa_especifica_seca_maxima_norma != null : temPontos,
      falta: validos > 0 && (!daBancada.balanca || !daBancada.balancaUmidade),
      resumo: plural(validos, 'válido', 'válidos')
        + (cb?.massa_especifica_seca_maxima_norma != null ? ' · curva fechada' : '') + naTentativa },
    ...(interpreta ? [{
      nome: 'Interpretação', feita: temPontos && !repAberta,
      resumo: repAberta ? `repetindo · tentativa ${d.repeticaoAberta}`
        : c?.umidade_otima_norma != null
          ? `w ót. ${num(c.umidade_otima_norma, CASAS.wOtima)} %` : 'depois dos pontos',
    }] : []),
    { nome: 'Fotos e laudo', feita: ficha?.status === 'aprovado' },
  ];

  const indicadores: Indicador[] = [
    { rotulo: 'Umidade ótima', chave: true, unidade: '%',
      valor: c?.umidade_otima_norma != null ? num(c.umidade_otima_norma, CASAS.wOtima) : null,
      porque: c?.curva_motivo ?? 'A curva ainda não fechou.',
      como: <>A abscissa do máximo da curva de compactação, expressa com
        0,1 % (NBR 7182:2025, 7.3).</> },
    { rotulo: 'γd máx', chave: true, unidade: 'g/cm³',
      valor: c?.massa_especifica_seca_maxima_norma != null
        ? num(c.massa_especifica_seca_maxima_norma, CASAS.gamaMax) : null,
      porque: c?.curva_motivo ?? 'A curva ainda não fechou.',
      como: <>A ordenada máxima da curva, expressa com 0,001 g/cm³
        (NBR 7182:2025, 7.2).</> },
    { rotulo: 'Pontos válidos', valor: String(validos) },
    { rotulo: 'Ramo seco / úmido',
      valor: `${c?.pontos_ramo_seco ?? 0} / ${c?.pontos_ramo_umido ?? 0}` },
    { rotulo: 'ρs', unidade: 'g/cm³',
      valor: c?.massa_especifica_graos != null ? num(c.massa_especifica_graos, 2) : null,
      porque: 'Sem ρs a curva de saturação não sai.',
      como: 'Da etapa 1. Sem ela o laudo sai sem a curva de saturação.' },
  ];

  return (
    <div className="tela compactacao ficha-carbon">
      <CabecalhoDaFicha id={id} sigla={ficha?.metodo_codigo ?? 'COMP'}
        titulo="Compactação" ficha={ficha} normaDaTela="ABNT NBR 7182"
        tentativaAberta={d?.repeticaoAberta} />
      <FaixaDaFicha indicadores={indicadores} itens={d.pendencias}
        pendencias={d.pendenciasDetalhadas} />
      <EtapasDaFicha etapas={etapas} atual={etapa} irPara={irPara} />

      {/*
        * A lista com código, `trava` e justificativa (0090). O componente
        * é o mesmo das outras fichas: um segundo desenho da mesma coisa é
        * como as duas telas passam a dizer coisas diferentes sobre a
        * mesma regra.
        */}
      <AncoraDePendencias>
        <Pendencias itens={d.pendencias} id={id}
          detalhe={d.pendenciasDetalhadas} aoJustificar={carregar} />
      </AncoraDePendencias>
      <RespostaDaAcao erro={erro} ok={ok ?? null} />
      {congelado && (
        <Aviso tipo="info" titulo={`Ensaio ${rotulo(ficha?.status).toLowerCase()} — só leitura`}>
          {ficha?.status === 'aprovado'
            ? 'Dado de ensaio aprovado não se altera. Correção é nova revisão: peça a reabertura a quem aprova.'
            : 'Ensaio encerrado não recebe dado novo. Se o serviço for refeito, é ensaio novo.'}
        </Aviso>
      )}

      {repAberta && etapa === 2 && (
        <AvisoDaRepeticaoAberta numero={d.repeticaoAberta!} interpreta={interpreta} />
      )}

      <fieldset className="so-leitura" disabled={congelado}>
      {etapa === 1 && (
        <Condicoes
          c={c} id={id} gravar={gravar} salvando={salvando}
          campoErro={campoErro} opcoesSoquete={opcoes(CAT_SOQUETE)}
          tabela={d.tabelaNorma ?? []} gente={gente} />
      )}

      {etapa === 2 && lendoRep && <Carregando texto="Lendo os pontos da repetição" />}

      {etapa === 2 && !lendoRep && (
        <>
          <Pontos key={repAberta ? `rep-${d.repeticaoAberta}` : 'vigente'}
            d={daBancada} id={id} gravar={gravar} salvando={salvando}
            campoErro={campoErro} equipamentos={equip}
            opcoesCilindro={opcoes(CAT_CILINDRO)}
            opcoesCapsula={opcoes(CAT_CAPSULA)}
            estufa={estufa} congelado={congelado}
            recarregar={async () => { await carregar(); setVersao((v) => v + 1); }} />
          <CurvaDeCompactacao d={daBancada} />
          {cb?.curva_motivo && (
            <Aviso tipo="atencao" titulo="A curva não fechou">{cb.curva_motivo}</Aviso>
          )}
          {c?.energia_conforme === false && (
            <Aviso tipo="atencao" titulo="Energia fora do previsto na norma">
              Você declarou {c.n_camadas} camada(s) e {c.n_golpes} golpe(s). A
              NBR 7182:2025 prevê {c.n_camadas_norma ?? '—'} e{' '}
              {c.n_golpes_norma ?? '—'} para essa energia.
            </Aviso>
          )}
        </>
      )}

      {interpreta && etapa === 3 && (
        <>
          {/* A repetição na Interpretação (onda 323, RN-260, D7: as curvas). */}
          <RepeticaoCompactacao id={id} congelado={congelado}
            opcoesCilindro={opcoes(CAT_CILINDRO)} opcoesCapsula={opcoes(CAT_CAPSULA)}
            versao={versao}
            aoMudar={async () => { await carregar(); setVersao((v) => v + 1); }}>
            <CurvaDeCompactacao d={d} />
          </RepeticaoCompactacao>
          <TentativasAnteriores ensaioId={id} ficha="compactacao" versao={versao}
            render={(r: any) => <PontosDaCompactacaoEmLeitura f={r} />} />
          <TrilhaDoEnsaio ensaioId={id} versao={versao} />
        </>
      )}

      {etapa === total && (
        <>
          <FotosDoRegistro
            id={id}
            titulo="Registro fotográfico"
            dica={'A moldagem e o desmonte entram no registro fotográfico do laudo.'}
            roteiro={[
              { tipo: 'cp_antes', nome: 'CP moldado', quando: 'antes do ensaio' },
              { tipo: 'cp_depois', nome: 'CP após o ensaio', quando: 'no desmonte' },
            ]}
          />
          <LaudoDaFicha id={id} />
        </>
      )}
      </fieldset>

      <RodapeDeEtapas etapas={etapas} atual={etapa} irPara={irPara}
        nota={ok ? 'Gravado — o resultado acima já é o do servidor.' : undefined} />
    </div>
  );
}

// ── 1 · condições ─────────────────────────────────────────────────────

interface GravarFn {
  (marca: string, chamada: () => Promise<EnsaioCompactacao>): Promise<boolean>;
}

function Condicoes({
  c, id, gravar, salvando, campoErro, opcoesSoquete, tabela, gente,
}: {
  c: any; id: string; gravar: GravarFn; salvando: string | null;
  campoErro(x: string): string | undefined; opcoesSoquete: Opcao[];
  tabela: any[]; gente: { id: string; nome: string }[];
}) {
  const [energia, setEnergia] = useState<string | null>(null);
  const [cilindro, setCilindro] = useState<string | null>(null);
  const [autorizador, setAutorizador] = useState<string | null>(null);
  const [justificativa, setJustificativa] = useState('');
  const [preparacao, setPreparacao] = useState<string | null>(null);
  const [reuso, setReuso] = useState(false);
  const [camadas, setCamadas] = useState('');
  const [golpes, setGolpes] = useState('');
  const [soquete, setSoquete] = useState<string | null>(null);
  const [rhoS, setRhoS] = useState('');

  useEffect(() => {
    setEnergia(c?.energia ?? null);
    setCilindro(c?.cilindro ?? null);
    setAutorizador(c?.energia_autorizada_por ?? null);
    setJustificativa(c?.energia_justificativa ?? '');
    setPreparacao(c?.preparacao ?? 'sem_secagem_previa');
    setReuso(Boolean(c?.reuso_material));
    setCamadas(c?.n_camadas != null ? paraCampo(c.n_camadas) : '');
    setGolpes(c?.n_golpes != null ? paraCampo(c.n_golpes) : '');
    setSoquete(c?.soquete_equipamento_id ?? null);
    setRhoS(c?.massa_especifica_graos != null
      ? paraCampo(c.massa_especifica_graos) : '');
  }, [c?.energia, c?.preparacao, c?.reuso_material, c?.n_camadas,
    c?.n_golpes, c?.soquete_equipamento_id, c?.massa_especifica_graos,
    c?.cilindro, c?.energia_autorizada_por, c?.energia_justificativa]);

  /*
   * Camadas e golpes da Tabela 1, preenchidos ao escolher cilindro+energia.
   *
   * A tabela vem do SERVIDOR (`tabelaNorma`), e não de um mapa escrito
   * aqui: um segundo lugar guardando a Tabela 1 é um segundo lugar para
   * ela ficar desatualizada — e quem recusa o INSERT é o banco, com a
   * RN-161.
   *
   * Preencher é conveniência; o técnico pode trocar. Se trocar, o banco
   * RECUSA, a menos que haja autorização registrada — e é por isso que o
   * bloco de exceção existe logo abaixo.
   */
  const daNorma = (cil: string | null, en: string | null) =>
    tabela.find((t: any) => t.cilindro === cil && t.energia === en);

  const preencher = (cil: string | null, en: string | null) => {
    const t = daNorma(cil, en);
    if (t) { setCamadas(String(t.n_camadas)); setGolpes(String(t.n_golpes)); }
  };

  const previsto = daNorma(cilindro, energia);
  const foraDaTabela = Boolean(previsto)
    && (Number(camadas) !== previsto.n_camadas
        || Number(golpes) !== previsto.n_golpes);

  const falta = !cilindro ? 'Falta escolher o cilindro.'
    : !energia ? 'Falta escolher a energia.'
      : !camadas || !golpes ? 'Faltam camadas e golpes.'
        : foraDaTabela && !autorizador ? 'Fora da Tabela 1: falta quem autoriza.'
          : foraDaTabela && justificativa.trim().length < 10
            ? 'Fora da Tabela 1: a justificativa tem pelo menos 10 caracteres.'
            : null;

  return (
    <BlocoDaFicha titulo="Condições do ensaio"
      sub="Cilindro e energia preenchem camadas e golpes pela Tabela 1 da NBR 7182:2025.">
      <div className="fe-miolo fe-campos">
        <Escolha
          rotulo="Cilindro" valor={cilindro}
          aoEscolher={(v) => { setCilindro(v); preencher(v, energia); }}
          limpavel={false}
          opcoes={[
            { valor: 'pequeno', texto: 'pequeno (Proctor, 1 000 cm³)' },
            { valor: 'grande', texto: 'grande (ISC, 2 085 cm³)' },
          ]}
          erro={campoErro('cilindro')}
        />
        <Escolha
          rotulo="Energia" valor={energia}
          aoEscolher={(v) => { setEnergia(v); preencher(cilindro, v); }}
          limpavel={false}
          opcoes={[
            { valor: 'normal', texto: 'normal' },
            { valor: 'intermediaria', texto: 'intermediária' },
            { valor: 'modificada', texto: 'modificada' },
          ]}
          erro={campoErro('energia')}
        />
        <Campo rotulo="Número de camadas" inputMode="numeric" value={camadas}
          onChange={(e) => setCamadas(e.target.value)}
          erro={campoErro('nCamadas')} />
        <Campo rotulo="Golpes por camada" inputMode="numeric" value={golpes}
          onChange={(e) => setGolpes(e.target.value)}
          erro={campoErro('nGolpes')} />
        <Escolha
          rotulo="Preparação" valor={preparacao} aoEscolher={setPreparacao}
          limpavel={false}
          opcoes={[
            { valor: 'sem_secagem_previa', texto: 'sem secagem prévia' },
            { valor: 'com_secagem_previa', texto: 'com secagem prévia' },
          ]}
        />
        <Escolha rotulo="Soquete" valor={soquete} aoEscolher={setSoquete}
          opcoes={opcoesSoquete} />
        <Campo rotulo="Massa específica dos sólidos (g/cm³)" inputMode="decimal"
          value={rhoS} onChange={(e) => setRhoS(e.target.value)}
          erro={campoErro('massaEspecificaGraos')}
          dica="Sem ela o laudo sai sem a curva de saturação." />
        <label className="marcavel">
          <input type="checkbox" checked={reuso}
            onChange={(e) => setReuso(e.target.checked)} />
          Reúso de material entre pontos
        </label>
      </div>
      {foraDaTabela && (
        <div className="fe-miolo">
          <Aviso tipo="atencao"
            titulo="Camadas ou golpes fora da Tabela 1 da NBR 7182:2025">
            Para essa combinação a norma prevê {previsto.n_camadas} camada(s) e
            {' '}{previsto.n_golpes} golpe(s). O sistema aceita o ensaio fora
            disso, mas só com autorização registrada — o laudo vai dizer.
            <Escolha rotulo="Autorizado por" valor={autorizador}
              aoEscolher={setAutorizador}
              opcoes={gente.map((g) => ({ valor: g.id, texto: g.nome }))} />
            <Campo rotulo="Justificativa" value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              erro={campoErro('energiaJustificativa')}
              dica="Mínimo de 10 caracteres. Fica no laudo e na auditoria." />
          </Aviso>
        </div>
      )}
      <div className="acoes fe-acoes">
        {falta && <p className="o-que-falta">{falta}</p>}
        <button type="button" className="principal"
          aria-disabled={falta !== null || salvando !== null}
          title={falta ?? undefined}
          onClick={() => {
            if (falta || salvando !== null) return;
            void gravar('cabecalho', () =>
              gravarCompactacaoCabecalho(id, {
                energia,
                cilindro,
                energiaAutorizadaPor: foraDaTabela
                  ? (autorizador ?? undefined) : undefined,
                energiaJustificativa: foraDaTabela
                  ? justificativa : undefined,
                preparacao,
                reusoMaterial: reuso,
                nCamadas: Number(paraNumero(camadas) ?? 0),
                nGolpes: Number(paraNumero(golpes) ?? 0),
                soqueteEquipamentoId: soquete ?? undefined,
                massaEspecificaGraos: paraNumero(rhoS),
              }));
          }}>
          {salvando === 'cabecalho' ? 'Gravando…' : 'Gravar condições'}
        </button>
      </div>
    </BlocoDaFicha>
  );
}

// ── 2 · pontos e suas cápsulas ────────────────────────────────────────

interface LinhaPonto { cilindro: string; massa: string }
interface LinhaCapsula { capsula: string; umido: string; seco: string }

function Pontos({
  d, id, gravar, salvando, campoErro, equipamentos, opcoesCilindro,
  opcoesCapsula, estufa, congelado, recarregar,
}: {
  d: EnsaioCompactacao; id: string; gravar: GravarFn; salvando: string | null;
  campoErro(x: string): string | undefined;
  equipamentos: ReturnType<typeof useEquipamentos>;
  opcoesCilindro: Opcao[]; opcoesCapsula: Opcao[];
  estufa: Estufa; congelado: boolean; recarregar(): Promise<void>;
}) {
  /* O cilindro do último ponto vira o padrão do próximo — quase sempre é
     o mesmo cilindro a noite inteira. */
  const ultimoCilindro = [...d.pontos].reverse()
    .find((p: any) => !p.descartado)?.cilindro_equipamento_id ?? '';
  const [novos, setNovos] = useState<LinhaPonto[]>(
    () => [{ cilindro: ultimoCilindro, massa: '' }]);
  const [balanca, setBalanca] = useState(d.balanca?.id ?? '');
  const [balancaUmidade, setBalancaUmidade] = useState(d.balancaUmidade?.id ?? '');
  useEffect(() => { setBalanca(d.balanca?.id ?? ''); }, [d.balanca?.id]);
  useEffect(() => { setBalancaUmidade(d.balancaUmidade?.id ?? ''); },
    [d.balancaUmidade?.id]);
  const [aberto, setAberto] = useState<string | null>(null);
  const [descartando, setDescartando] = useState<any | null>(null);

  const proxima = d.pontos.reduce(
    (m: number, p: any) => Math.max(m, p.ordem), 0) + 1;
  const prontos = novos.filter((l) => l.cilindro && l.massa);
  const mudouBalanca = (balanca && balanca !== (d.balanca?.id ?? ''))
    || (balancaUmidade && balancaUmidade !== (d.balancaUmidade?.id ?? ''));

  const falta = prontos.length && !balanca ? 'Falta escolher a balança do cilindro.'
    : prontos.length || mudouBalanca ? null
      : novos.some((l) => l.cilindro || l.massa)
        ? (novos.find((l) => !l.cilindro) ? 'Falta escolher o cilindro.'
          : 'Falta a massa de cilindro + solo.')
        : 'Escolha o cilindro e digite a massa de cilindro + solo para gravar.';

  const mexer = (i: number, campo: keyof LinhaPonto, v: string) =>
    setNovos((ls) => ls.map((l, j) => (j === i ? { ...l, [campo]: v } : l)));

  const gravarPontos = async () => {
    const feito = await gravar('pontos', () =>
      gravarCompactacaoPontos(id, {
        ...(balanca ? { balancaEquipamentoId: balanca } : {}),
        ...(balancaUmidade ? { balancaUmidadeEquipamentoId: balancaUmidade } : {}),
        pontos: prontos.map((l, i) => ({
          ordem: proxima + i,
          cilindroEquipamentoId: l.cilindro,
          massaCilindroSolo: paraNumero(l.massa),
        })),
      }));
    if (feito) {
      const ult = prontos[prontos.length - 1]?.cilindro ?? ultimoCilindro;
      setNovos([{ cilindro: ult, massa: '' }]);
    }
  };

  const colunas: ColunaDeLancamento[] = [
    { texto: 'Ponto' },
    { texto: 'Cilindro' },
    { texto: 'Cilindro + solo (g)', num: true },
    { texto: 'γ (g/cm³)', num: true },
    { texto: 'w (%)', num: true },
    { texto: 'γd (g/cm³)', num: true },
    { texto: 'Cápsulas' },
    { texto: '' },
  ];

  const lancados: LinhaDeLancamento[] = d.pontos.map((p: any) => {
    const caps = d.umidades.filter((u: any) => u.ponto_id === p.id && !u.descartada).length;
    const naEstufa = d.umidades.filter((u: any) => u.ponto_id === p.id
      && naEstufaDaFicha(u)).length;
    return {
      chave: p.id,
      classe: p.descartado ? 'descartada' : aberto === p.id ? 'aberta' : undefined,
      titulo: <>Ponto {p.ordem} · <span className="cod">{p.cilindro}</span></>,
      resultado: p.descartado ? '—'
        : <span className="n">γd {num(p.massa_especifica_seca, CASAS.gama)}</span>,
      nota: p.descartado ? `Descartado: ${p.motivo_descarte ?? ''}` : undefined,
      semCartao: [0, 1, 5],
      celulas: [
        p.ordem,
        <>
          <span className="cod">{p.cilindro}</span>
          {p.volume_cilindro == null && <Selo tom="critico">Sem volume calibrado</Selo>}
        </>,
        num(p.massa_cilindro_solo, 2),
        num(p.massa_especifica_umida, CASAS.gama),
        num(p.umidade_media, CASAS.wPonto),
        <b className="fe-w">{num(p.massa_especifica_seca, CASAS.gama)}</b>,
        <button type="button" className="botao discreto pequeno"
          aria-pressed={aberto === p.id}
          onClick={() => setAberto(aberto === p.id ? null : p.id)}>
          {caps}{naEstufa ? ` (${naEstufa} na estufa)` : ''} · {aberto === p.id ? 'fechar' : 'abrir'}
        </button>,
        p.descartado
          ? <span className="fe-motivo" title={p.motivo_descarte}><Selo tom="neutro">Descartado</Selo></span>
          : (
            <button type="button" className="botao discreto pequeno fe-descartar"
              aria-label={`Descartar o ponto ${p.ordem}`}
              disabled={salvando !== null}
              onClick={() => setDescartando(p)}>
              Descartar
            </button>
          ),
      ],
    };
  });

  const novasLinhas: LinhaDeLancamento[] = novos.map((l, i) => ({
    chave: `novo-${i}`,
    classe: 'nova',
    titulo: `Ponto ${proxima + i} · novo`,
    semCartao: [0, 3, 4, 5, 6],
    celulas: [
      <span className="sub">{proxima + i}</span>,
      /* 29 cilindros CBR na lista: digitar «07» é um gesto, procurar
         «CBR-07» entre eles é uma caçada (Testesolo, 01/09/2026). */
      <EscolhaPorCodigo rotulo="" opcoes={opcoesCilindro.map(comoCodigo)}
        valor={l.cilindro} aoEscolher={(v) => mexer(i, 'cilindro', v)} />,
      <CampoNumerico rotulo="Massa de cilindro + solo (g)" rotuloOculto
        value={l.massa} onChange={(e) => mexer(i, 'massa', e.target.value)} />,
      <span className="sub">—</span>,
      <span className="sub">—</span>,
      <span className="sub">—</span>,
      <span className="sub">—</span>,
      <button type="button" className="remover" tabIndex={-1}
        aria-label={`Remover o ponto ${proxima + i}`}
        title="Remover esta linha (ainda não gravada)"
        onClick={() => setNovos((ls) => ls.filter((_, j) => j !== i))}>
        ×
      </button>,
    ],
  }));

  const pontoAberto = d.pontos.find((p: any) => p.id === aberto);
  const validos = d.pontos.filter((p: any) => !p.descartado).length;

  return (
    <BlocoDaFicha titulo="Pontos"
      selo={validos ? <Selo tom="ok">{plural(validos, 'válido', 'válidos')}</Selo> : undefined}
      sub="Cada ponto é uma linha; abra a linha para as cápsulas de umidade dele. O volume e a massa do cilindro vêm da calibração interna — não se digitam.">
      <div className="fe-miolo fe-campos">
        <EscolhaDeBalanca rotulo="Balança do cilindro + solo"
          equipamentos={equipamentos} valor={balanca} aoEscolher={setBalanca}
          erro={campoErro('balancaEquipamentoId')} />
        <EscolhaDeBalanca rotulo="Balança das cápsulas"
          equipamentos={equipamentos} valor={balancaUmidade}
          aoEscolher={setBalancaUmidade}
          erro={campoErro('balancaUmidadeEquipamentoId')} />
      </div>

      <div className="fe-tabela">
        <div className="fe-tabela-topo">
          <h3>Pontos da curva <small>{d.pontos.length
            ? `${d.pontos.length} lançados` : 'nenhum lançado ainda'}</small></h3>
          <button type="button" className="botao discreto pequeno"
            onClick={() => setNovos((ls) => [...ls,
              { cilindro: ls[ls.length - 1]?.cilindro ?? ultimoCilindro, massa: '' }])}>
            + ponto
          </button>
        </div>
        <TabelaDeLancamento rotulo="Pontos da curva — lançados e novos"
          classe="fe-lanc" colunas={colunas} linhas={[...lancados, ...novasLinhas]} />
        {/*
          * A barra que acompanha a rolagem no celular (onda 163): com
          * cinco pontos lançados, o gesto seguinte ficava a uma tela e
          * meia do topo. `.acoes-fixas` vale só abaixo de 640 px.
          */}
        <div className="acoes acoes-fixas fe-acoes">
          {falta
            ? <p className="o-que-falta">{falta}</p>
            : <p className="fe-lembrete">Linha em branco não sobe; ponto errado se descarta com motivo.</p>}
          <button type="button" className="principal" data-gravar-tabela=""
            aria-disabled={falta !== null || salvando !== null}
            title={falta ?? undefined}
            onClick={() => { if (!falta && salvando === null) void gravarPontos(); }}>
            {salvando === 'pontos' ? 'Gravando…' : 'Gravar pontos'}
          </button>
        </div>
      </div>

      {pontoAberto && (
        <Capsulas ponto={pontoAberto}
          linhas={d.umidades.filter((u: any) => u.ponto_id === pontoAberto.id)}
          id={id} gravar={gravar} salvando={salvando}
          opcoesCapsula={opcoesCapsula} balancaUmidade={balancaUmidade}
          balancaUmidadeGravada={d.balancaUmidade?.id ?? ''}
          estufa={estufa} congelado={congelado} recarregar={recarregar} />
      )}

      {opcoesCilindro.length === 0 && (
        <div className="fe-miolo">
          <Aviso tipo="atencao">
            Não há cilindro de compactação cadastrado com calibração interna. Sem o
            volume calibrado o sistema não calcula γ — e não vai inventar um.
          </Aviso>
        </div>
      )}

      <JanelaDeDescarte
        alvo={descartando ? `o ponto ${descartando.ordem} · ${descartando.cilindro ?? ''}` : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={(motivo) => gravar('descarte',
          () => descartarCompactacaoPonto(id, descartando.id, motivo))}
        salvando={salvando !== null}
      />
    </BlocoDaFicha>
  );
}

function Capsulas({
  ponto, linhas, id, gravar, salvando, opcoesCapsula, balancaUmidade,
  balancaUmidadeGravada, estufa, congelado, recarregar,
}: {
  ponto: any; linhas: any[]; id: string; gravar: GravarFn;
  salvando: string | null; opcoesCapsula: Opcao[];
  balancaUmidade: string; balancaUmidadeGravada: string;
  estufa: Estufa; congelado: boolean; recarregar(): Promise<void>;
}) {
  const [novas, setNovas] = useState<LinhaCapsula[]>(
    () => [{ capsula: '', umido: '', seco: '' }]);
  const [descartando, setDescartando] = useState<any | null>(null);
  useEffect(() => { setNovas([{ capsula: '', umido: '', seco: '' }]); }, [ponto.id]);

  const proxima = linhas.reduce(
    (m: number, u: any) => Math.max(m, u.ordem), 0) + 1;
  /* A seca é opcional: sem ela, a cápsula vai para a estufa (RN-241). */
  const prontas = novas.filter((l) => l.capsula && l.umido);
  const falta = prontas.length && !balancaUmidade
    ? 'Falta escolher a balança das cápsulas (acima).'
    : prontas.length ? null
      : !novas.some((l) => l.capsula || l.umido || l.seco)
        ? 'Preencha a cápsula e a massa úmida para gravar.'
        : novas.find((l) => !l.capsula) ? 'Falta escolher a cápsula.'
          : 'Falta a massa de cápsula + solo úmido.';
  const naEstufa = linhas.filter(naEstufaDaFicha).length;

  const mexer = (i: number, campo: keyof LinhaCapsula, v: string) =>
    setNovas((ls) => ls.map((l, j) => (j === i ? { ...l, [campo]: v } : l)));

  const colunas: ColunaDeLancamento[] = [
    { texto: 'Det.' },
    { texto: 'Cápsula' },
    { texto: 'Tara (g)', num: true },
    { texto: 'Cáps. + úmido (g)', num: true },
    { texto: 'Cáps. + seco (g)', num: true },
    { texto: 'w (%)', num: true },
    { texto: '' },
  ];

  /*
   * As determinações do ponto. No celular cada cápsula vira um cartão,
   * com a umidade calculada no cabeçalho dele (onda 163).
   */
  const lancadas: LinhaDeLancamento[] = linhas.map((u: any) => ({
    chave: u.id,
    classe: u.descartada ? 'descartada' : undefined,
    titulo: <>Determinação {u.ordem} · <span className="cod">{u.capsula ?? '—'}</span></>,
    resultado: u.descartada ? '—' : naEstufaDaFicha(u) ? 'na estufa'
      : <span className="n">{num(u.umidade, CASAS.wCapsula)} %</span>,
    semCartao: [0, 1, 5],
    celulas: [
      u.ordem,
      <span className="cod">{u.capsula}</span>,
      u.tara == null ? <Selo tom="critico">Sem calibração interna</Selo> : num(u.tara, 2),
      num(u.massa_capsula_solo_umido, 2),
      <SecaDaLinha linha={u} estufa={estufa} congelado={congelado || ponto.descartado}
        opcoesCapsula={opcoesCapsula} aoMudou={recarregar} />,
      <b className="fe-w"><WDaLinha linha={u} casas={CASAS.wCapsula} /></b>,
      u.descartada
        ? <span className="fe-motivo" title={u.motivo_descarte}><Selo tom="neutro">Descartada</Selo></span>
        : (
          <button type="button" className="botao discreto pequeno fe-descartar"
            aria-label={`Descartar a determinação ${u.ordem}`}
            disabled={salvando !== null}
            onClick={() => setDescartando(u)}>
            Descartar
          </button>
        ),
    ],
  }));

  const novasLinhas: LinhaDeLancamento[] = ponto.descartado ? [] : novas.map((l, i) => ({
    chave: `nova-${i}`,
    classe: 'nova',
    titulo: `Determinação ${proxima + i} · nova`,
    semCartao: [0, 2, 5],
    celulas: [
      <span className="sub">{proxima + i}</span>,
      <EscolhaPorCodigo rotulo="" opcoes={opcoesCapsula.map(comoCodigo)}
        valor={l.capsula} aoEscolher={(v) => mexer(i, 'capsula', v)} />,
      <span className="sub">—</span>,
      <CampoNumerico rotulo="Cápsula + solo úmido (g)" rotuloOculto
        value={l.umido} onChange={(e) => mexer(i, 'umido', e.target.value)} />,
      <CampoNumerico rotulo="Cápsula + solo seco (g)" rotuloOculto
        value={l.seco} onChange={(e) => mexer(i, 'seco', e.target.value)} />,
      <span className="sub">—</span>,
      <button type="button" className="remover" tabIndex={-1}
        aria-label={`Remover a determinação ${proxima + i}`}
        title="Remover esta linha (ainda não gravada)"
        onClick={() => setNovas((ls) => ls.filter((_, j) => j !== i))}>
        ×
      </button>,
    ],
  }));

  return (
    <div className="fe-tabela fe-filha">
      <div className="fe-tabela-topo">
        <h3>Cápsulas do ponto {ponto.ordem} <small>{ponto.cilindro}</small></h3>
        {!ponto.descartado && (
          <button type="button" className="botao discreto pequeno"
            onClick={() => setNovas((ls) => [...ls, { capsula: '', umido: '', seco: '' }])}>
            + linha
          </button>
        )}
      </div>
      <TabelaDeLancamento rotulo={`Determinações de umidade do ponto ${ponto.ordem}`}
        classe="fe-lanc" colunas={colunas} linhas={[...lancadas, ...novasLinhas]} />
      {!ponto.descartado && !congelado && <LembreteDaEstufa quantas={naEstufa} />}
      {!ponto.descartado && (
        <div className="acoes fe-acoes">
          {falta
            ? <p className="o-que-falta">{falta}</p>
            : <p className="fe-lembrete">O w do ponto é a média das cápsulas válidas.</p>}
          <button type="button" className="botao secundario" data-gravar-tabela=""
            aria-disabled={falta !== null || salvando !== null}
            title={falta ?? undefined}
            onClick={async () => {
              if (falta || salvando !== null) return;
              const feito = await gravar('umidades', () =>
                gravarCompactacaoUmidades(id, ponto.id, {
                  ...(balancaUmidade && balancaUmidade !== balancaUmidadeGravada
                    ? { balancaUmidadeEquipamentoId: balancaUmidade } : {}),
                  determinacoes: prontas.map((l, i) => ({
                    ordem: proxima + i,
                    capsulaEquipamentoId: l.capsula,
                    massaCapsulaSoloUmido: paraNumero(l.umido),
                    ...(paraNumero(l.seco) != null
                      ? { massaCapsulaSoloSeco: paraNumero(l.seco) } : {}),
                  })),
                }));
              if (feito) setNovas([{ capsula: '', umido: '', seco: '' }]);
            }}>
            {salvando === 'umidades' ? 'Gravando…' : `Gravar cápsulas do ponto ${ponto.ordem}`}
          </button>
        </div>
      )}
      <JanelaDeDescarte
        alvo={descartando ? `a determinação ${descartando.ordem} · ${descartando.capsula ?? ''}` : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={(motivo) => gravar('descarte',
          () => descartarCompactacaoUmidade(id, descartando.id, motivo))}
        salvando={salvando !== null}
      />
    </div>
  );
}

// ── a curva na tela ───────────────────────────────────────────────────

/**
 * A curva de compactação, com a de saturação (onda 237, decisão 3).
 *
 * Reconstruída exatamente como o laudo (`compactacao-documento.service`):
 * a reta do ramo seco até `w0`, a cúbica de Hermite entre `w0` e `w1`, e
 * a reta do ramo úmido depois de `w1` — com os coeficientes que
 * `compactacao_curva` (0022) resolveu. Nada é ajustado aqui. Se o pico do
 * desenho discordar do γd máx do banco, a tela diz — como o laudo.
 *
 * A saturação é a da NBR 7182:2025, seção 6 (S = 100 %, ρw = 1,00 g/cm³),
 * e só sai com ρs informado: uma linha desenhada a partir de um ρs
 * presumido seria uma afirmação que ninguém fez.
 */
/**
 * A curva de uma leitura da ficha, reconstruída com os coeficientes que o
 * banco resolveu (0022): os pontos válidos, o traço e o pico.
 */
export function curvaDaCompactacao(d: EnsaioCompactacao | null | undefined) {
  const bons = (d?.pontos ?? []).filter((p: any) => !p.descartado
    && p.umidade_media != null && p.massa_especifica_seca != null);
  const ws = bons.map((p: any) => Number(p.umidade_media));
  const k = d?.curva;
  const temCurva = !!k && k.w0 != null && k.w1 != null && bons.length >= 2;
  const linha: { x: number; y: number }[] = [];
  let pico: number | null = null;
  let wot: number | null = null;
  let wmin = ws.length ? Math.min(...ws) : 0;
  let wmax = ws.length ? Math.max(...ws) : 0;
  if (temCurva) {
    const w0 = Number(k.w0); const w1 = Number(k.w1);
    const g0 = Number(k.a_seco) * w0 + Number(k.b_seco);
    const g1 = Number(k.a_umido) * w1 + Number(k.b_umido);
    const m0 = Number(k.a_seco); const m1 = Number(k.a_umido);
    const h = w1 - w0;
    const emW = (w: number) => {
      const t = (w - w0) / h;
      return (2 * t ** 3 - 3 * t ** 2 + 1) * g0 + (t ** 3 - 2 * t ** 2 + t) * h * m0
        + (-2 * t ** 3 + 3 * t ** 2) * g1 + (t ** 3 - t ** 2) * h * m1;
    };
    wmin = Math.min(wmin, w0); wmax = Math.max(wmax, w1);
    const folga = (wmax - wmin) * 0.08;
    linha.push({ x: wmin - folga, y: m0 * (wmin - folga) + Number(k.b_seco) });
    linha.push({ x: w0, y: g0 });
    for (let i = 1; i < 60; i++) {
      const w = w0 + (h * i) / 60;
      linha.push({ x: w, y: emW(w) });
    }
    linha.push({ x: w1, y: g1 });
    linha.push({ x: wmax + folga, y: m1 * (wmax + folga) + Number(k.b_umido) });
    if (k.umidade_otima != null) { wot = Number(k.umidade_otima); pico = emW(wot); }
  }
  const doBanco = k?.massa_especifica_seca_maxima != null
    ? Number(k.massa_especifica_seca_maxima) : null;
  return { bons, ws, temCurva, linha, pico, wot, wmin, wmax, doBanco };
}

export function CurvaDeCompactacao({ d, inicial, titulo, acao }: {
  d: EnsaioCompactacao;
  /**
   * A tentativa inicial, na repetição (onda 323): a curva tracejada e os
   * pontos vazados sob os da atual — os pontos ganham `data-serie`.
   */
  inicial?: EnsaioCompactacao | null;
  titulo?: string;
  /** Um controle no topo do bloco («mostrar a inicial»). */
  acao?: ReactNode;
}) {
  const nome = titulo ?? 'Curva de compactação';
  const a = curvaDaCompactacao(d);
  const ini = inicial ? curvaDaCompactacao(inicial) : null;
  const bons = a.bons;
  const todos = [...bons, ...(ini?.bons ?? [])];
  if (todos.length < 2) {
    return (
      <div className="bloco fe-bloco fe-grafico">
        <header className="fe-bloco-topo"><h2><span>{nome}</span></h2>{acao}</header>
        <p className="sub">A curva aparece com dois pontos válidos.</p>
      </div>
    );
  }
  const ws = todos.map((p: any) => Number(p.umidade_media));
  const gs = todos.map((p: any) => Number(p.massa_especifica_seca));
  const temCurva = a.temCurva;
  const linha = a.linha;
  const pico = a.pico;
  const wot = a.wot;
  const doBanco = a.doBanco;
  const wmin = Math.min(...ws, ...(a.temCurva ? [a.wmin] : []), ...(ini?.temCurva ? [ini.wmin] : []));
  const wmax = Math.max(...ws, ...(a.temCurva ? [a.wmax] : []), ...(ini?.temCurva ? [ini.wmax] : []));
  const diverge = pico != null && doBanco != null && Math.abs(pico - doBanco) > 0.0005;

  const rhoS = d.cabecalho?.massa_especifica_graos != null
    ? Number(d.cabecalho.massa_especifica_graos) : null;
  const sat = rhoS && rhoS > 0
    ? Array.from({ length: 41 }, (_, i) => {
      const w = Math.min(...ws) * 0.85 + ((Math.max(...ws) * 1.15 - Math.min(...ws) * 0.85) * i) / 40;
      return { x: w, y: rhoS / (1 + (w / 100) * rhoS) };
    })
    : [];

  const folgaX = Math.max(0.5, (wmax - wmin) * 0.15);
  const ymin = Math.min(...gs);
  const ymax = Math.max(...gs, pico ?? 0, ini?.pico ?? 0);
  const folgaY = Math.max(0.01, (ymax - ymin) * 0.25);
  const janela = { x0: wmin - folgaX, x1: wmax + folgaX, y0: ymin - folgaY, y1: ymax + folgaY };

  return (
    <div className="bloco fe-bloco fe-grafico">
      <header className="fe-bloco-topo">
        <h2><span>{nome}</span></h2>
        {acao ?? <Selo tom="neutro">A mesma do laudo</Selo>}
      </header>
      <div className="fe-miolo">
        <Plano janela={janela} tituloX="Teor de umidade, w (%)"
          tituloY="Massa específica seca, γd (g/cm³)">
          {({ px }) => (
            <>
              {sat.length > 0 && (
                <path className="serie saturacao" d={sat.map((p, i) => {
                  const q = px(p);
                  return `${i ? 'L' : 'M'}${q.x.toFixed(2)} ${q.y.toFixed(2)}`;
                }).join(' ')} />
              )}
              {ini && ini.linha.length > 0 && (
                <Serie pontos={ini.linha} px={px} marcar={false}
                  classe="repeticao-serie-inicial" curva="inicial" />
              )}
              {ini?.bons.map((p: any) => {
                const q = px({ x: Number(p.umidade_media), y: Number(p.massa_especifica_seca) });
                return <circle key={`i${p.id}`} className="ponto repeticao-ponto-inicial"
                  data-serie="inicial" cx={q.x} cy={q.y} r={4.5} />;
              })}
              {linha.length > 0 && (
                <Serie pontos={linha} px={px} marcar={false}
                  classe={inicial ? 'repeticao-serie-atual' : undefined}
                  curva={inicial ? 'atual' : undefined} />
              )}
              {bons.map((p: any) => {
                const q = px({ x: Number(p.umidade_media), y: Number(p.massa_especifica_seca) });
                return <circle key={p.id} className="ponto" data-serie={inicial ? 'atual' : undefined}
                  cx={q.x} cy={q.y} r={4} />;
              })}
              {wot != null && doBanco != null && (
                <Achado p={{ x: wot, y: doBanco }} px={px}
                  rotulo={`γd máx = ${num(doBanco, CASAS.gamaMax)}`} />
              )}
            </>
          )}
        </Plano>
        <p className="fe-legenda">
          {inicial && <span><i className="repeticao-l-inicial" />inicial (fora da conta)</span>}
          {inicial && <span><i className="l-aux" />curva inicial</span>}
          {inicial && <span><i className="repeticao-l-atual" />atual</span>}
          <span><i className={inicial ? 'repeticao-l-reta-atual' : 'l-reta'} />
            {inicial ? 'curva atual' : 'curva ajustada'}</span>
          {sat.length > 0
            ? <span><i className="l-sat" />saturação 100 % (ρs {num(rhoS, 2)})</span>
            : <span>sem ρs — sem curva de saturação</span>}
        </p>
        {!temCurva && (
          <p className="sub">A curva ajustada aparece quando os dois ramos tiverem pontos.</p>
        )}
        {diverge && (
          <Aviso tipo="erro" titulo="O desenho discorda do resultado">
            O pico da curva desenhada não bate com o γd máx calculado. Não emita
            o laudo — avise a Testesolo.
          </Aviso>
        )}
      </div>
    </div>
  );
}
